import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import produce from 'immer';
import React, {useState, useEffect, useCallback} from 'react';

import {assert} from 'Asserts';
import DocumentStore from 'notebook/documents/DocumentStore';
import DilutionsTable from 'procedures/design/method-config/DilutionsTable';
import useDraftMethodEditor from 'procedures/design/method-config/useDraftMethodEditor';
import {PROCEDURE_PRODUCT_LIMITS} from 'procedures/design/ProcedureDesignConstants';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {ProcedureValidationErrorType} from 'procedures/design/validation/ProcedureValidationTypes';
import {
  filterProcedureValidationErrors,
  findTestsTabValidationErrors,
} from 'procedures/design/validation/procedureValidationUtils';
import {
  InaccessibleProcedureTestDefinition,
  Method,
  MethodNode,
  MethodUpdater,
  ProcedureTestDefinition,
} from 'procedures/ProcedureDomainTypes';
import {
  BlockingSaveObjectSource,
  BlockingSaveOperation,
  ProcedurePageContext,
} from 'procedures/ProcedurePageContext';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import MethodSidepanelContext from 'procedures/processes/MethodSidepanelContext';
import {saveWithValidationGate} from 'procedures/processes/useValidateBeforeSave';
import {ProcedurePauseCondition} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/reduceFlowchartConfig';
import getTestProps from 'test/lib/cypress/getTestProps';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import TestDefinitionsTableAdapter from '../../tables/TestDefinitionsTableAdapter';
import DilutionAssignmentSelector from './DilutionAssignmentSelector';
import {MethodEditorTabSavingButtons, styles as sharedStyles} from './MethodEditorTabShared';
import TestDefinitionsTable from './TestDefinitionsTable';
import useStartEditingLegacyDefinitions from './useStartEditingLegacyDefinitions';

// Ensure saveImmediately calls throw all errors (including 400s) so we can handle them uniquely
const SHOULD_SAVE_IMMEDIATELY_BUBBLE_UP_ERRORS = true;
export interface TestTablesEditorProps {
  isEditingMethodTab: boolean;
  isProcedureReadOnly: boolean;
  editingMethodCid: string;
  selectedMethod: Method;
  onChangeMethod: (cid: string, methodUpdater: MethodUpdater) => void;
  updateIsEditingState: (newState: boolean) => void;
}

const stripEmptyRowsMiddleware = stripEmptyRows<MethodNode>;

function stripEmptyRows<
  T extends {
    testDefinitions: Array<ProcedureTestDefinition | InaccessibleProcedureTestDefinition>;
  },
>(method: T): T {
  return produce(method, (draftMethod) => {
    draftMethod.testDefinitions = draftMethod.testDefinitions.filter(
      (td) => !TestDefinitionsTableAdapter.isTestDefinitionEmpty(td)
    );
  });
}

export default function TestTablesEditor(props: TestTablesEditorProps): JSX.Element {
  const {saveImmediately} = MethodSidepanelContext.useContext();
  return saveImmediately == null ? <TestTablesEditorLegacy {...props} /> : <TestTablesEditorPoW {...props} />;
}

function TestTablesEditorLegacy({
  isEditingMethodTab,
  isProcedureReadOnly,
  editingMethodCid,
  selectedMethod,
  onChangeMethod,
  updateIsEditingState,
}: TestTablesEditorProps): JSX.Element {
  const {
    legacySaveImmediately,
    resetLegacyDraftFlowchartChanges,
    registerLegacyPreSaveImmediatelyMiddleware,
    unregisterLegacyPreSaveImmediatelyMiddleware,
    procedureMetadata,
  } = ProcedurePageContext.useContext();
  const {procedureType} = procedureMetadata;
  const procedureId = procedureMetadata.id;
  const [middlewareId, setMiddlewareId] = useState<string | null>(null);
  const [isTableReadyForEditing, setIsTableReadyForEditing] = useState(false);

  const {procedureValidationErrors, setAnyTabValidationErrors} = ProcedureValidationContext.useContext();
  const relevantProcedureValidationErrors = findTestsTabValidationErrors(
    procedureValidationErrors,
    editingMethodCid
  );
  const productLimitErrors = filterProcedureValidationErrors(procedureValidationErrors, {
    errorTypes: [ProcedureValidationErrorType.PROCEDURE_PRODUCT_LIMITS],
  });

  const {handleOnStartEditing, editingState} = useStartEditingLegacyDefinitions({
    isEditingMethodTab,
    procedureId,
    updateIsEditingState,
  });

  const shouldBlockEditingDefinitions = !isEditingMethodTab || isProcedureReadOnly;

  const anyErrors = relevantProcedureValidationErrors.length > 0 || productLimitErrors.length > 0;
  const errorText =
    productLimitErrors.length > 0
      ? `Test definitions table can't be submitted because the number of tests in your ${getProcedureUIString(
          ProceduresStringKeys.PROCEDURE,
          procedureType,
          {stringCase: StringCase.LOWER}
        )} exceeds ${PROCEDURE_PRODUCT_LIMITS.TEST_DEFINITIONS_PER_PROCEDURE}`
      : 'Please fix errors in the tables below';

  // On unmount, we ensure that the error status is reset, and any middleware is removed
  useEffect(() => {
    setAnyTabValidationErrors(false);
    if (middlewareId != null) {
      unregisterLegacyPreSaveImmediatelyMiddleware(middlewareId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register the middleware for removing empty rows in test definition table
  useEffect(() => {
    if (middlewareId != null) {
      unregisterLegacyPreSaveImmediatelyMiddleware(middlewareId);
    }
    if (isEditingMethodTab) {
      const newMiddlewareId = registerLegacyPreSaveImmediatelyMiddleware(
        selectedMethod.cid,
        stripEmptyRowsMiddleware
      );
      setMiddlewareId(newMiddlewareId);
    } else {
      setMiddlewareId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingMethodTab, selectedMethod.cid]);

  return (
    <div
      className={classNames(css(styles.testsWrapper), css(sharedStyles.detailsContainer))}
      {...getTestProps({component: 'TestTablesEditor', element: 'root'})}
    >
      <div className={css(sharedStyles.detailsHeader)}>
        <div>
          <FieldLabel label="Test definitions" />
          <p>Test definitions specify the type of results expected, including for multiplex assays.</p>
        </div>
        {!isProcedureReadOnly && (
          <MethodEditorTabSavingButtons
            editingState={editingState}
            anyErrors={anyErrors}
            errorText={errorText}
            areTablesStillLoading={!isTableReadyForEditing}
            handleOnCancel={() => {
              resetLegacyDraftFlowchartChanges();
              updateIsEditingState(false);
            }}
            handleOnSubmit={async () => {
              try {
                // This call requires an immediate API call, with an overlay. The middleware (above) handles cleanup tasks
                await legacySaveImmediately(
                  () => ({
                    blockingSaveObjectSource: BlockingSaveObjectSource.TestDefinition,
                    blockingSaveOperation: BlockingSaveOperation.Update,
                  }),
                  'Saving test definitions...',
                  SHOULD_SAVE_IMMEDIATELY_BUBBLE_UP_ERRORS
                );
                // When saving test definitions, we need to clear the saved documents in memory so
                // that the user fetches them with any new changes applied
                DocumentStore.resetState();
                updateIsEditingState(false);
              } catch (e) {
                if (e.status === 400) {
                  // Swallow 400 errors to prevent duplicate toasts
                  // Reset the draft changes if there are validation errors
                  resetLegacyDraftFlowchartChanges();
                  updateIsEditingState(false);
                } else {
                  throw e;
                }
              }
            }}
            handleOnEdit={handleOnStartEditing}
          />
        )}
      </div>
      <div className={css(sharedStyles.detailsContent)}>
        <div className={css(sharedStyles.detailsContentScrollContainer)}>
          <TestDefinitionsTable
            method={selectedMethod}
            onChangeMethod={onChangeMethod}
            onReadyForEditing={() => {
              setIsTableReadyForEditing(true);
            }}
            locked={shouldBlockEditingDefinitions}
          />
          <DilutionsTable
            method={selectedMethod}
            onChangeMethod={onChangeMethod}
            locked={shouldBlockEditingDefinitions}
          />
          <DilutionAssignmentSelector
            method={selectedMethod}
            onChangeMethod={onChangeMethod}
            locked={shouldBlockEditingDefinitions}
          />
        </div>
      </div>
    </div>
  );
}

// The two omitted props were used in legacy procedures to manage what can now be done locally. The "lose unsaved
// changes?" modal was owned by ProcedurePage, and these props were passed down on context. Now that modal is
// owned by the workflows platform. See useDraftMethodEditor for more details.
type TestTablesEditorPoWProps = Omit<TestTablesEditorProps, 'isEditingMethodTab' | 'updateIsEditingState'>;

function TestTablesEditorPoW({
  isProcedureReadOnly,
  editingMethodCid,
  selectedMethod,
  onChangeMethod: _onChangeMethod,
}: TestTablesEditorPoWProps): JSX.Element {
  const {procedureMetadata} = ProcedurePageContext.useContext();
  const {procedureType} = procedureMetadata;
  const [isTableReadyForEditing, setIsTableReadyForEditing] = useState(false);
  const {saveImmediately, validateBeforeSave} = MethodSidepanelContext.useContext();
  assert(
    saveImmediately != null && validateBeforeSave != null,
    'TestTablesEditorPoW can only run in a PoW context'
  );

  // This is only called when the user explicitly presses the Save button. It's further wrapped by
  // the useDraftMethodEditor hook below.
  const _onSave = useCallback(
    async (method: Method): Promise<boolean> => {
      const strippedMethod = stripEmptyRows(method);
      const didSave = await saveWithValidationGate({
        validateBeforeSave,
        saveImmediately,
        methodData: strippedMethod,
      });
      if (didSave) {
        // When saving test definitions, we need to clear the saved documents in memory so
        // that the user fetches them with any new changes applied
        DocumentStore.resetState();
      }
      return didSave;
    },
    [saveImmediately, validateBeforeSave]
  );

  const {
    isEditing,
    editingState,
    onStartEditing,
    onSave,
    onCancel,
    onUpdateMethod,
    methodDuringEditing,
    newValidationContextValue,
  } = useDraftMethodEditor({
    method: selectedMethod,
    procedureType,
    onSave: _onSave,
    onChangeMethod: _onChangeMethod,
    pauseCondition: ProcedurePauseCondition.IS_EDITING_PARAMETERS,
  });

  const {procedureValidationErrors, setAnyTabValidationErrors} = newValidationContextValue;
  const relevantProcedureValidationErrors = findTestsTabValidationErrors(
    procedureValidationErrors,
    editingMethodCid
  );
  const productLimitErrors = filterProcedureValidationErrors(procedureValidationErrors, {
    errorTypes: [ProcedureValidationErrorType.PROCEDURE_PRODUCT_LIMITS],
  });

  const shouldBlockEditingDefinitions = !isEditing || isProcedureReadOnly;
  const anyErrors = relevantProcedureValidationErrors.length > 0 || productLimitErrors.length > 0;
  const errorText =
    productLimitErrors.length > 0
      ? `Test definitions table can't be submitted because the number of tests in your ${getProcedureUIString(
          ProceduresStringKeys.PROCEDURE,
          procedureType,
          {stringCase: StringCase.LOWER}
        )} exceeds ${PROCEDURE_PRODUCT_LIMITS.TEST_DEFINITIONS_PER_PROCEDURE}`
      : 'Please fix errors in the tables below';

  // On unmount, we ensure that the error status is reset, and any middleware is removed
  useEffect(() => {
    setAnyTabValidationErrors(false);
  }, [setAnyTabValidationErrors]);

  return (
    <ProcedureValidationContext.Provider value={newValidationContextValue}>
      <div
        className={classNames(css(styles.testsWrapper), css(sharedStyles.detailsContainer))}
        {...getTestProps({component: 'TestTablesEditor', element: 'root'})}
      >
        <div className={css(sharedStyles.detailsHeader)}>
          <div>
            <FieldLabel label="Test definitions" />
            <p>Test definitions specify the type of results expected, including for multiplex assays.</p>
          </div>
          {!isProcedureReadOnly && (
            <MethodEditorTabSavingButtons
              editingState={editingState}
              anyErrors={anyErrors}
              errorText={errorText}
              areTablesStillLoading={!isTableReadyForEditing}
              handleOnCancel={onCancel}
              handleOnSubmit={onSave}
              handleOnEdit={onStartEditing}
            />
          )}
        </div>
        <div className={css(sharedStyles.detailsContent)}>
          <div className={css(sharedStyles.detailsContentScrollContainer)}>
            <TestDefinitionsTable
              method={methodDuringEditing}
              onChangeMethod={onUpdateMethod}
              onReadyForEditing={() => {
                setIsTableReadyForEditing(true);
              }}
              locked={shouldBlockEditingDefinitions}
            />
            <DilutionsTable
              method={methodDuringEditing}
              onChangeMethod={onUpdateMethod}
              locked={shouldBlockEditingDefinitions}
            />
            <DilutionAssignmentSelector
              method={methodDuringEditing}
              onChangeMethod={onUpdateMethod}
              locked={shouldBlockEditingDefinitions}
            />
          </div>
        </div>
      </div>
    </ProcedureValidationContext.Provider>
  );
}

export const styles = StyleSheet.create({
  testsWrapper: {
    marginTop: '4px',
    marginLeft: '4px',
  },
});
